import { useState, useEffect } from 'react';
import {
  Loader2, Linkedin, Youtube, Instagram, Music2, CheckCircle,
  XCircle, Send, Settings2, Key, Trash2, Play, RefreshCw, Eye, EyeOff,
  ExternalLink, AlertTriangle, Link2, Shield
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CHANNELS = [
  { id: 'linkedin_company', label: 'LinkedIn (Company)', icon: Linkedin, color: '#0077B5', oauth: true,
    credentialFields: [
      { key: 'access_token', label: 'Access Token', type: 'password', help: 'Managed via OAuth — connect below' },
      { key: 'organization_urn', label: 'Organization URN', type: 'text', help: 'e.g. urn:li:organization:123456789' },
    ]},
  { id: 'linkedin_personal', label: 'LinkedIn (Personal)', icon: Linkedin, color: '#0A66C2', oauth: true,
    credentialFields: [
      { key: 'access_token', label: 'Access Token', type: 'password', help: 'Managed via OAuth' },
      { key: 'person_urn', label: 'Person URN', type: 'text', help: 'e.g. urn:li:person:AbCdEf123' },
    ]},
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: '#E4405F',
    credentialFields: [
      { key: 'access_token', label: 'Page Access Token', type: 'password', help: 'Facebook Page long-lived access token' },
      { key: 'business_account_id', label: 'Business Account ID', type: 'text', help: 'Instagram Business Account ID from Graph API' },
    ]},
  { id: 'tiktok', label: 'TikTok', icon: Music2, color: '#000000',
    credentialFields: [
      { key: 'access_token', label: 'Access Token', type: 'password', help: 'OAuth access token from TikTok Developer Portal' },
    ]},
  { id: 'youtube', label: 'YouTube', icon: Youtube, color: '#FF0000',
    credentialFields: [
      { key: 'client_id', label: 'Client ID', type: 'text', help: 'Google Cloud OAuth 2.0 Client ID' },
      { key: 'client_secret', label: 'Client Secret', type: 'password', help: 'Google Cloud OAuth 2.0 Client Secret' },
      { key: 'refresh_token', label: 'Refresh Token', type: 'password', help: 'OAuth 2.0 refresh token' },
    ]},
];

const ContentDistributionTab = ({ headers }) => {
  const [config, setConfig] = useState(null);
  const [queue, setQueue] = useState([]);
  const [queueCounts, setQueueCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedChannel, setExpandedChannel] = useState(null);
  const [credentialValues, setCredentialValues] = useState({});
  const [credentialStatus, setCredentialStatus] = useState({});
  const [testingChannel, setTestingChannel] = useState(null);
  const [testResult, setTestResult] = useState({});
  const [savingCreds, setSavingCreds] = useState(null);
  const [showValues, setShowValues] = useState({});
  const [dispatching, setDispatching] = useState(false);
  const [queueFilter, setQueueFilter] = useState('all');
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [postingLatest, setPostingLatest] = useState(false);
  const [postResult, setPostResult] = useState(null);
  const [linkedinMsg, setLinkedinMsg] = useState(null);

  // Check URL params for linkedin callback result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const liResult = params.get('linkedin');
    if (liResult === 'connected') {
      setLinkedinMsg({ type: 'success', text: 'LinkedIn connected successfully!' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (liResult === 'error') {
      const reason = params.get('reason') || 'Unknown error';
      setLinkedinMsg({ type: 'error', text: `LinkedIn connection failed: ${decodeURIComponent(reason)}` });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchAll = async () => {
    try {
      const [configRes, queueRes, liRes] = await Promise.all([
        fetch(`${API}/api/content-distribution/config`, { headers, credentials: 'include' }),
        fetch(`${API}/api/content-distribution/queue`, { headers, credentials: 'include' }),
        fetch(`${API}/api/linkedin/status`, { headers, credentials: 'include' }),
      ]);
      if (configRes.ok) setConfig(await configRes.json());
      if (queueRes.ok) {
        const d = await queueRes.json();
        setQueue(d.items || []);
        setQueueCounts(d.counts || {});
      }
      if (liRes.ok) setLinkedinStatus(await liRes.json());
    } catch {
      setConfig({ channels: CHANNELS.map(c => ({ id: c.id, label: c.label, enabled: false, connected: false })), auto_distribute: false, content_types: ['blog', 'news'] });
    }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveConfig = async (updates) => {
    setSaving(true);
    const updated = { ...config, ...updates };
    setConfig(updated);
    try {
      await fetch(`${API}/api/content-distribution/config`, {
        method: 'PUT', headers, credentials: 'include', body: JSON.stringify(updated),
      });
    } catch (e) { console.error('Failed to save distribution config:', e); }
    finally { setSaving(false); }
  };

  const toggleChannel = (channelId) => {
    const channels = (config?.channels || []).map(ch =>
      ch.id === channelId ? { ...ch, enabled: !ch.enabled } : ch
    );
    saveConfig({ channels });
  };

  const fetchCredentials = async (channelId) => {
    try {
      const res = await fetch(`${API}/api/content-distribution/credentials/${channelId}`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCredentialStatus(prev => ({ ...prev, [channelId]: data.fields }));
      }
    } catch (e) { console.error('Failed to fetch credentials:', e); }
  };

  const handleExpandChannel = (channelId) => {
    if (expandedChannel === channelId) {
      setExpandedChannel(null);
    } else {
      setExpandedChannel(channelId);
      fetchCredentials(channelId);
    }
  };

  const saveCredentials = async (channelId) => {
    setSavingCreds(channelId);
    try {
      const res = await fetch(`${API}/api/content-distribution/credentials/${channelId}`, {
        method: 'PUT', headers, credentials: 'include', body: JSON.stringify(credentialValues[channelId] || {}),
      });
      if (res.ok) {
        setCredentialValues(prev => ({ ...prev, [channelId]: {} }));
        fetchCredentials(channelId);
        fetchAll();
        setTestResult(prev => ({ ...prev, [channelId]: null }));
      }
    } catch (e) { console.error('Failed to save credentials:', e); }
    finally { setSavingCreds(null); }
  };

  const deleteCredentials = async (channelId) => {
    if (!window.confirm('Remove all credentials for this channel?')) return;
    try {
      await fetch(`${API}/api/content-distribution/credentials/${channelId}`, { method: 'DELETE', headers, credentials: 'include' });
      setCredentialStatus(prev => ({ ...prev, [channelId]: {} }));
      setTestResult(prev => ({ ...prev, [channelId]: null }));
      fetchAll();
    } catch (e) { console.error('Failed to delete credentials:', e); }
  };

  const testConnection = async (channelId) => {
    setTestingChannel(channelId);
    setTestResult(prev => ({ ...prev, [channelId]: null }));
    try {
      const res = await fetch(`${API}/api/content-distribution/test/${channelId}`, { method: 'POST', headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTestResult(prev => ({ ...prev, [channelId]: data }));
        fetchAll();
      }
    } catch (e) {
      setTestResult(prev => ({ ...prev, [channelId]: { success: false, error: 'Connection error' } }));
    }
    finally { setTestingChannel(null); }
  };

  const connectLinkedin = async () => {
    try {
      const res = await fetch(`${API}/api/linkedin/connect`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Redirect current window (callback will redirect back)
        window.location.href = data.auth_url;
      }
    } catch {}
  };

  const postLatestBlog = async () => {
    setPostingLatest(true);
    setPostResult(null);
    try {
      const res = await fetch(`${API}/api/linkedin/post-latest`, { method: 'POST', headers, credentials: 'include' });
      const data = await res.json();
      if (res.ok) {
        setPostResult({ success: true, text: `Posted "${data.article}" to LinkedIn`, post_urn: data.post_urn });
      } else {
        setPostResult({ success: false, text: data.detail || 'Failed to post' });
      }
    } catch (e) {
      setPostResult({ success: false, text: 'Network error' });
    }
    finally { setPostingLatest(false); }
  };

  const setOrganization = async (urn) => {
    try {
      await fetch(`${API}/api/linkedin/organization`, {
        method: 'PUT', headers, credentials: 'include', body: JSON.stringify({ org_urn: urn }),
      });
      fetchAll();
    } catch {}
  };

  const dispatchAll = async () => {
    setDispatching(true);
    try {
      await fetch(`${API}/api/content-distribution/dispatch-all`, { method: 'POST', headers, credentials: 'include' });
      fetchAll();
    } catch (e) { console.error('Failed to dispatch all:', e); }
    finally { setDispatching(false); }
  };

  const dispatchItem = async (itemId) => {
    try {
      await fetch(`${API}/api/content-distribution/dispatch/${itemId}`, { method: 'POST', headers, credentials: 'include' });
      fetchAll();
    } catch (e) { console.error('Failed to dispatch item:', e); }
  };

  const deleteItem = async (itemId) => {
    try {
      await fetch(`${API}/api/content-distribution/queue/${itemId}`, { method: 'DELETE', headers, credentials: 'include' });
      setQueue(q => q.filter(i => i.id !== itemId));
    } catch (e) { console.error('Failed to delete queue item:', e); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-taxera-blue" /></div>;

  const filteredQueue = queueFilter === 'all' ? queue : queue.filter(i => i.status === queueFilter);
  const liConnected = linkedinStatus?.connected && linkedinStatus?.valid;

  return (
    <div className="space-y-6" data-testid="admin-content-distribution">
      {/* LinkedIn callback message */}
      {linkedinMsg && (
        <div className={`p-4 rounded-xl flex items-center gap-3 text-sm ${
          linkedinMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`} data-testid="linkedin-callback-msg">
          {linkedinMsg.type === 'success' ? <CheckCircle className="w-5 h-5 shrink-0" /> : <XCircle className="w-5 h-5 shrink-0" />}
          <span className="flex-1">{linkedinMsg.text}</span>
          <button onClick={() => setLinkedinMsg(null)} className="text-xs opacity-60 hover:opacity-100">Dismiss</button>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-taxera-blue/5 to-taxera-teal/5 rounded-2xl border border-taxera-blue/10 p-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-lg bg-taxera-blue/10 flex items-center justify-center">
            <Send className="w-4 h-4 text-taxera-blue" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-taxera-charcoal">Content Distribution Pipeline</h3>
            <p className="text-xs text-slate-400">Distribute blog & news content across social channels</p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button onClick={() => saveConfig({ auto_distribute: !config?.auto_distribute })}
            className={`w-10 h-6 rounded-full transition-colors relative ${config?.auto_distribute ? 'bg-taxera-teal' : 'bg-slate-300'}`}
            data-testid="distribution-auto-toggle">
            <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${config?.auto_distribute ? 'left-5' : 'left-1'}`} />
          </button>
          <span className="text-sm font-medium text-slate-600">Auto-distribute new content</span>
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        </div>
      </div>

      {/* Channels */}
      <div>
        <h3 className="text-sm font-bold text-taxera-charcoal mb-3">Distribution Channels</h3>
        <div className="space-y-3">
          {CHANNELS.map((channel) => {
            const Icon = channel.icon;
            const chConfig = config?.channels?.find(c => c.id === channel.id) || {};
            const isExpanded = expandedChannel === channel.id;
            const cStatus = credentialStatus[channel.id] || {};
            const allCredsSet = Object.values(cStatus).length > 0 && Object.values(cStatus).every(f => f.is_set);
            const tResult = testResult[channel.id];
            const isLinkedin = channel.id.startsWith('linkedin_');
            const oauthManaged = isLinkedin && liConnected;

            return (
              <div key={channel.id}
                className={`rounded-2xl border transition-all ${chConfig.enabled ? 'border-taxera-teal/40 bg-white' : 'border-slate-200 bg-white'}`}
                data-testid={`channel-${channel.id}`}>

                {/* Channel Header */}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${channel.color}12` }}>
                      <Icon className="w-4 h-4" style={{ color: channel.color }} />
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-taxera-charcoal">{channel.label}</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${
                          oauthManaged ? 'bg-emerald-500' :
                          chConfig.connected ? 'bg-emerald-500' :
                          chConfig.reconnect_required ? 'bg-red-500' :
                          allCredsSet ? 'bg-amber-400' : 'bg-slate-300'
                        }`} />
                        <span className="text-2xs font-medium text-slate-400">
                          {oauthManaged ? 'Connected via OAuth' :
                           chConfig.reconnect_required ? 'Reconnect required' :
                           chConfig.connected ? 'Connected & verified' :
                           allCredsSet ? 'Credentials set — test connection' :
                           isLinkedin ? 'Connect via OAuth below' : 'API credentials required'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleExpandChannel(channel.id)}
                      className={`p-2 rounded-lg transition-colors ${isExpanded ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                      data-testid={`channel-settings-${channel.id}`}>
                      <Settings2 className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>
                    <button onClick={() => toggleChannel(channel.id)}
                      className={`w-9 h-5 rounded-full transition-colors relative ${chConfig.enabled ? 'bg-taxera-teal' : 'bg-slate-300'}`}
                      data-testid={`channel-toggle-${channel.id}`}>
                      <div className={`w-3.5 h-3.5 bg-white rounded-full absolute top-[3px] transition-all ${chConfig.enabled ? 'left-[18px]' : 'left-[3px]'}`} />
                    </button>
                  </div>
                </div>

                {/* Expanded: LinkedIn OAuth or Manual Credentials */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-slate-100 pt-4">
                    {isLinkedin ? (
                      <LinkedInChannelSettings
                        channel={channel}
                        linkedinStatus={linkedinStatus}
                        onConnect={connectLinkedin}
                        onTestConnection={() => testConnection(channel.id)}
                        testingChannel={testingChannel}
                        tResult={tResult}
                        onPostLatest={postLatestBlog}
                        postingLatest={postingLatest}
                        postResult={postResult}
                        onSetOrg={setOrganization}
                      />
                    ) : (
                      <ManualCredentialSettings
                        channel={channel}
                        cStatus={cStatus}
                        credentialValues={credentialValues}
                        setCredentialValues={setCredentialValues}
                        showValues={showValues}
                        setShowValues={setShowValues}
                        savingCreds={savingCreds}
                        onSave={() => saveCredentials(channel.id)}
                        onTest={() => testConnection(channel.id)}
                        onDelete={() => deleteCredentials(channel.id)}
                        testingChannel={testingChannel}
                        tResult={tResult}
                        allCredsSet={allCredsSet}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Content Types */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-sm font-bold text-taxera-charcoal mb-3">Content to Distribute</h3>
        <div className="flex flex-wrap gap-2">
          {['blog', 'news', 'case_studies', 'whitepapers'].map(type => {
            const active = config?.content_types?.includes(type);
            return (
              <button key={type} onClick={() => {
                const types = active
                  ? (config.content_types || []).filter(t => t !== type)
                  : [...(config.content_types || []), type];
                saveConfig({ content_types: types });
              }}
                className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${
                  active ? 'bg-taxera-blue text-white' : 'bg-slate-100 text-slate-400 border border-slate-200'
                }`}
                data-testid={`content-type-${type}`}>
                {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </button>
            );
          })}
        </div>
      </div>

      {/* Distribution Queue */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-taxera-charcoal">Distribution Queue</h3>
            <div className="flex items-center gap-3 mt-1">
              {Object.entries(queueCounts).filter(([k]) => k !== 'total').map(([k, v]) => (
                <span key={k} className="text-2xs text-slate-400">{k}: <span className="font-semibold text-slate-600">{v}</span></span>
              ))}
            </div>
          </div>
          <button onClick={dispatchAll} disabled={dispatching || !queue.some(i => i.status === 'pending')}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-taxera-teal text-white rounded-full text-xs font-semibold hover:shadow-lg transition-all disabled:opacity-50"
            data-testid="dispatch-all-btn">
            {dispatching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
            Dispatch All Pending
          </button>
        </div>

        <div className="flex gap-1 px-5 pt-3">
          {['all', 'pending', 'delivered', 'failed', 'awaiting_credentials'].map(f => (
            <button key={f} onClick={() => setQueueFilter(f)}
              className={`text-2xs px-2.5 py-1 rounded-full font-semibold transition-colors ${
                queueFilter === f ? 'bg-taxera-charcoal text-white' : 'bg-slate-100 text-slate-400'
              }`}>
              {f === 'awaiting_credentials' ? 'Awaiting' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {filteredQueue.length === 0 ? (
          <div className="text-center py-10 px-5">
            <Send className="w-7 h-7 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No items in the queue.</p>
            <p className="text-xs text-slate-300 mt-1">Enable auto-distribution or add items manually.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {filteredQueue.map((item) => {
              const ch = CHANNELS.find(c => c.id === item.channel);
              const ChIcon = ch?.icon || Send;
              return (
                <div key={item.id} className="px-5 py-3 flex items-center gap-3" data-testid={`queue-item-${item.id}`}>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${ch?.color || '#666'}12` }}>
                    <ChIcon className="w-3.5 h-3.5" style={{ color: ch?.color || '#666' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-taxera-charcoal truncate">{item.content_title || 'Untitled'}</div>
                    <div className="text-2xs text-slate-400">
                      {ch?.label || item.channel} — {new Date(item.created_at).toLocaleDateString()}
                      {item.error && <span className="text-red-400 ml-2">{item.error.substring(0, 60)}</span>}
                    </div>
                  </div>
                  <span className={`text-2xs px-2 py-0.5 rounded-full font-semibold shrink-0 ${
                    item.status === 'delivered' ? 'bg-emerald-50 text-emerald-600' :
                    item.status === 'failed' ? 'bg-red-50 text-red-600' :
                    item.status === 'awaiting_credentials' ? 'bg-amber-50 text-amber-600' :
                    'bg-blue-50 text-blue-600'
                  }`}>
                    {item.status === 'awaiting_credentials' ? 'Awaiting' : item.status}
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    {(item.status === 'pending' || item.status === 'failed') && (
                      <button onClick={() => dispatchItem(item.id)}
                        className="p-1.5 rounded-lg bg-blue-50 text-blue-500 hover:bg-blue-100 transition-colors" title="Dispatch">
                        <Play className="w-3 h-3" />
                      </button>
                    )}
                    <button onClick={() => deleteItem(item.id)}
                      className="p-1.5 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 transition-colors" title="Delete">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};


/* ─── LinkedIn Channel Settings (OAuth-managed) ─── */
const LinkedInChannelSettings = ({ channel, linkedinStatus, onConnect, onTestConnection, testingChannel, tResult, onPostLatest, postingLatest, postResult, onSetOrg }) => {
  const li = linkedinStatus || {};
  const connected = li.connected && li.valid;

  return (
    <div className="space-y-4">
      {/* Connection status */}
      {connected ? (
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-testid="linkedin-connected-status">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-semibold text-emerald-700">Connected</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
            {li.person_name && <div>Authorized by: <span className="font-semibold">{li.person_name}</span></div>}
            {li.org_urn && <div>Organization: <span className="font-mono text-2xs">{li.org_urn}</span></div>}
            <div>Token expires: <span className="font-semibold">{li.expires_in_days} days</span></div>
            {li.has_refresh_token && <div>Refresh token: <span className="font-semibold">{li.refresh_expires_in_days != null ? `${li.refresh_expires_in_days} days` : 'Active'}</span></div>}
          </div>
        </div>
      ) : li.reconnect_required ? (
        <div className="bg-red-50 rounded-xl p-4 border border-red-200" data-testid="linkedin-reconnect-warning">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <span className="text-sm font-semibold text-red-700">Reconnect Required</span>
          </div>
          <p className="text-xs text-red-600">The LinkedIn token has expired and could not be refreshed. Please reconnect.</p>
        </div>
      ) : (
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid="linkedin-not-connected">
          <p className="text-sm text-slate-500 mb-3">Connect your LinkedIn account to enable company page posting.</p>
        </div>
      )}

      {/* Connect / Reconnect button */}
      <button onClick={onConnect}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0077B5] text-white rounded-xl text-xs font-semibold hover:bg-[#005f8f] transition-colors"
        data-testid="linkedin-connect-btn">
        <Linkedin className="w-4 h-4" />
        {connected ? 'Reconnect LinkedIn' : 'Connect with LinkedIn'}
        <ExternalLink className="w-3 h-3 opacity-60" />
      </button>

      {/* Organization selector (if multiple discovered) */}
      {li.discovered_orgs?.length > 1 && (
        <div>
          <label className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">Select Organization</label>
          <div className="space-y-1">
            {li.discovered_orgs.map(org => (
              <button key={org.urn} onClick={() => onSetOrg(org.urn)}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                  org.urn === li.org_urn ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}>
                <span className="font-semibold">{org.name}</span>
                <span className="text-2xs ml-2 opacity-60">{org.role}</span>
                {org.urn === li.org_urn && <CheckCircle className="w-3 h-3 inline ml-2" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Granted scopes */}
      {li.granted_scopes && (
        <div data-testid="linkedin-scopes-info">
          <div className="flex items-center gap-1.5 mb-1">
            <Shield className="w-3 h-3 text-slate-400" />
            <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide">Granted Scopes</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {li.granted_scopes.split(' ').filter(Boolean).map(scope => (
              <span key={scope} className="text-2xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full font-mono">{scope}</span>
            ))}
          </div>
        </div>
      )}

      {/* Redirect URI */}
      {li.redirect_uri && (
        <div data-testid="linkedin-redirect-uri">
          <div className="flex items-center gap-1.5 mb-1">
            <Link2 className="w-3 h-3 text-slate-400" />
            <span className="text-2xs font-bold text-slate-400 uppercase tracking-wide">Redirect URI</span>
          </div>
          <code className="text-2xs bg-slate-50 px-2 py-1 rounded text-slate-500 break-all block">{li.redirect_uri}</code>
        </div>
      )}

      {/* Actions row */}
      {connected && (
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <button onClick={onTestConnection}
            disabled={testingChannel === channel.id}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-200 transition-colors disabled:opacity-50"
            data-testid={`cred-test-${channel.id}`}>
            {testingChannel === channel.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Test Connection
          </button>
          {channel.id === 'linkedin_company' && (
            <button onClick={onPostLatest}
              disabled={postingLatest}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0077B5]/10 text-[#0077B5] rounded-lg text-xs font-semibold hover:bg-[#0077B5]/20 transition-colors disabled:opacity-50"
              data-testid="linkedin-post-latest-btn">
              {postingLatest ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Post Latest Blog
            </button>
          )}
        </div>
      )}

      {/* Test result */}
      {tResult && (
        <div className={`p-3 rounded-lg text-xs flex items-start gap-2 ${tResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}
          data-testid={`test-result-${channel.id}`}>
          {tResult.success ? <CheckCircle className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
          <div>
            <span className="font-semibold">{tResult.success ? 'Connected' : 'Connection failed'}</span>
            {tResult.account && <span className="ml-1 text-emerald-500">({tResult.account})</span>}
            {tResult.error && <p className="mt-1 text-2xs">{tResult.error}</p>}
          </div>
        </div>
      )}

      {/* Post result */}
      {postResult && (
        <div className={`p-3 rounded-lg text-xs flex items-start gap-2 ${postResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}
          data-testid="linkedin-post-result">
          {postResult.success ? <CheckCircle className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
          <span>{postResult.text}</span>
        </div>
      )}
    </div>
  );
};


/* ─── Manual Credential Settings (non-LinkedIn channels) ─── */
const ManualCredentialSettings = ({ channel, cStatus, credentialValues, setCredentialValues, showValues, setShowValues, savingCreds, onSave, onTest, onDelete, testingChannel, tResult, allCredsSet }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <Key className="w-3.5 h-3.5 text-slate-400" />
      <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">API Credentials</span>
    </div>
    <div className="space-y-2.5">
      {channel.credentialFields.map(field => {
        const status = cStatus[field.key];
        const isPassword = field.type === 'password';
        const showKey = `${channel.id}_${field.key}`;
        const isShown = showValues[showKey];
        return (
          <div key={field.key}>
            <div className="flex items-center justify-between mb-1">
              <label className="text-2xs font-bold text-slate-400 uppercase tracking-wide">{field.label}</label>
              {status?.is_set && (
                <span className="text-2xs px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded-full font-semibold flex items-center gap-1">
                  <CheckCircle className="w-2.5 h-2.5" /> Set
                </span>
              )}
            </div>
            <div className="flex gap-1.5">
              <div className="relative flex-1">
                <input
                  type={isPassword && !isShown ? 'password' : 'text'}
                  value={credentialValues[channel.id]?.[field.key] || ''}
                  onChange={e => setCredentialValues(prev => ({
                    ...prev, [channel.id]: { ...(prev[channel.id] || {}), [field.key]: e.target.value }
                  }))}
                  placeholder={status?.is_set ? (status.masked_value || '••••••••') : field.help}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-taxera-teal font-mono"
                  data-testid={`cred-${channel.id}-${field.key}`}
                />
                {isPassword && (
                  <button type="button" onClick={() => setShowValues(prev => ({ ...prev, [showKey]: !isShown }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {isShown ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
    <div className="flex items-center gap-2 mt-4">
      <button onClick={onSave}
        disabled={savingCreds === channel.id || !credentialValues[channel.id] || Object.values(credentialValues[channel.id] || {}).every(v => !v)}
        className="inline-flex items-center gap-1.5 px-4 py-2 bg-taxera-blue text-white rounded-lg text-xs font-semibold hover:shadow-md transition-all disabled:opacity-50"
        data-testid={`cred-save-${channel.id}`}>
        {savingCreds === channel.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Key className="w-3 h-3" />}
        Save Credentials
      </button>
      <button onClick={onTest}
        disabled={testingChannel === channel.id}
        className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-200 transition-colors disabled:opacity-50"
        data-testid={`cred-test-${channel.id}`}>
        {testingChannel === channel.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        Test Connection
      </button>
      {allCredsSet && (
        <button onClick={onDelete}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-500 rounded-lg text-xs font-semibold hover:bg-red-100 transition-colors"
          data-testid={`cred-delete-${channel.id}`}>
          <Trash2 className="w-3 h-3" /> Remove
        </button>
      )}
    </div>
    {tResult && (
      <div className={`mt-3 p-3 rounded-lg text-xs flex items-start gap-2 ${tResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}
        data-testid={`test-result-${channel.id}`}>
        {tResult.success ? <CheckCircle className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
        <div>
          <span className="font-semibold">{tResult.success ? 'Connected' : 'Connection failed'}</span>
          {tResult.account && <span className="ml-1 text-emerald-500">({tResult.account})</span>}
          {tResult.error && <p className="mt-1 text-2xs">{tResult.error}</p>}
        </div>
      </div>
    )}
  </div>
);


export default ContentDistributionTab;
